import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import { api } from '../api/client'
import { getTierByPoints } from '../data/tiers'
import PageMeta from '../components/PageMeta'
import { lkClass } from '../components/lk/Place'
import QuizLanguageSelect from '../components/QuizLanguageSelect'

/* ────────────────────────────── Guest Header ────────────────────────────── */

function GuestHeader() {
  const { t } = useTranslation()
  // Scroll-spy: this page is the introduction, so "Giới thiệu" is active except
  // over the leaderboard preview ("Xếp hạng"). "Trang chủ" leads back to the
  // one-screen crossroads at "/".
  const [active, setActive] = useState<'features' | 'leaderboard'>('features')
  useEffect(() => {
    if (typeof IntersectionObserver === 'undefined') return
    const els = ['features', 'leaderboard']
      .map((id) => document.getElementById(id))
      .filter((el): el is HTMLElement => el != null)
    if (els.length === 0) return
    const visible: Record<string, boolean> = {}
    const obs = new IntersectionObserver(
      (entries) => {
        for (const e of entries) visible[e.target.id] = e.isIntersecting
        setActive(visible.leaderboard ? 'leaderboard' : 'features')
      },
      // A thin band around the viewport's vertical center. (A zero-height
      // "-50%/-50%" line makes isIntersecting flaky — zero-area intersection.)
      { rootMargin: '-45% 0px -45% 0px' },
    )
    els.forEach((el) => obs.observe(el))
    return () => obs.disconnect()
  }, [])
  // Smooth-scroll to an on-page section (no id = back to top) instead of
  // routing away — the leaderboard preview lives lower on this same page.
  const scrollTo = (id?: string) => (e: React.MouseEvent) => {
    e.preventDefault()
    if (!id) {
      window.scrollTo({ top: 0, behavior: 'smooth' })
      return
    }
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' })
  }
  const navBase = 'font-body tracking-tight transition-colors duration-300'
  const navActive = 'text-bq-amberd border-b-2 border-bq-amber pb-1'
  const navIdle = 'text-bq-ink2 hover:text-bq-ink'
  return (
    <nav className="fixed top-0 w-full z-50 flex justify-between items-center px-4 sm:px-6 py-3 sm:py-4 bg-bq-white/90 backdrop-blur border-b border-bq-hair shadow-bq-soft">
      <div className="flex items-center gap-4 sm:gap-8 max-w-7xl mx-auto w-full">
        <Link to="/" className="text-xl sm:text-2xl font-bold tracking-tighter text-bq-amberd font-display no-underline">BibleQuiz</Link>
        <div className="hidden md:flex gap-6 items-center flex-1">
          <Link to="/" className={`${navBase} ${navIdle}`}>
            {t('nav.home')}
          </Link>
          <a
            href="#leaderboard"
            onClick={scrollTo('leaderboard')}
            className={`${navBase} ${active === 'leaderboard' ? navActive : navIdle}`}
          >
            {t('nav.leaderboard')}
          </a>
          <a
            href="#features"
            onClick={scrollTo('features')}
            className={`${navBase} ${active === 'features' ? navActive : navIdle}`}
          >
            {t('landing.about')}
          </a>
        </div>
        <div className="flex items-center gap-2 sm:gap-4 ml-auto">
          <QuizLanguageSelect className="hidden sm:flex" />
          <Link
            to="/login"
            className="px-2 sm:px-4 py-2 text-sm font-semibold text-bq-ink2 hover:text-bq-ink transition-colors active:scale-95 whitespace-nowrap"
          >
            {t('auth.login')}
          </Link>
          <Link
            to="/login"
            className="bg-bq-action px-4 sm:px-6 py-2 rounded-xl text-bq-ink shadow-bq-action font-bold text-sm sm:text-base active:scale-95 transition-transform inline-block whitespace-nowrap"
          >
            {t('auth.register')}
          </Link>
        </div>
      </div>
    </nav>
  )
}

/* ────────────────────────────── Hero Section ────────────────────────────── */

function HeroSection() {
  const { t } = useTranslation()
  return (
    <header className="relative pt-20 sm:pt-24 pb-10 sm:pb-16 px-4 sm:px-6">
      <div className="max-w-7xl mx-auto relative rounded-bq overflow-hidden border-[3px] border-bq-ink shadow-bq-card bg-bq-cream">
        {/* the village gate at dawn, the traveller about to set out */}
        <img
          src="/images/lk/place-gate.webp"
          alt=""
          aria-hidden
          className="block w-full h-[240px] sm:h-[340px] md:h-[540px] object-cover"
          style={{ objectPosition: '50% 62%' }}
        />
        <img
          src="/images/lk/hero.webp"
          alt=""
          aria-hidden
          className={`absolute right-[8%] bottom-[6%] h-[120px] sm:h-[150px] md:h-[230px] drop-shadow-[0_6px_0_rgba(29,43,34,.25)] ${lkClass.bob}`}
        />
        <div className="md:absolute md:inset-y-0 md:left-0 p-4 sm:p-6 md:p-10 flex items-center">
          <div className="max-w-xl bg-bq-white/90 border-[3px] border-bq-ink rounded-bq shadow-bq-card p-6 md:p-8 space-y-5">
            <span className="inline-flex items-center gap-1.5 px-3 py-0.5 rounded-full bg-bq-amber border-2 border-bq-ink text-[13px] font-extrabold">
              <img src="/images/lk/lantern-on.webp" alt="" aria-hidden className="h-4" />
              {t('landing.tagline')}
            </span>
            <h1 className="m-0 font-display text-[36px] sm:text-[44px] md:text-[54px] font-extrabold leading-[1.06] text-bq-ink">
              {t('landing.heroTitle1')}{t('landing.heroHighlight')}{t('landing.heroTitle2')}
            </h1>
            <p className="m-0 font-read text-[16px] sm:text-[18px] text-bq-ink2 leading-relaxed">
              {t('landing.heroDesc')}
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <Link to="/practice" className="lk-btn text-bq-ink text-[17px] no-underline">
                {t('landing.tryNow')}
              </Link>
              <Link to="/login" className="lk-btn !bg-bq-white text-bq-ink text-[17px] no-underline">
                {t('auth.login')}
              </Link>
            </div>
          </div>
        </div>
      </div>
    </header>
  )
}

/* ────────────────────────────── Features Grid ───────────────────────────── */

const featureKeys = [
  { icon: 'menu_book', titleKey: 'landing.feature1Title', descKey: 'landing.feature1Desc', accent: 'text-bq-sapphire', tint: 'bg-bq-sapphire/10', edge: 'group-hover:border-bq-sapphire' },
  { icon: 'stars', titleKey: 'landing.feature2Title', descKey: 'landing.feature2Desc', accent: 'text-bq-amberd', tint: 'bg-bq-amber/10', edge: 'group-hover:border-bq-amber' },
  { icon: 'church', titleKey: 'landing.feature3Title', descKey: 'landing.feature3Desc', accent: 'text-bq-emerald', tint: 'bg-bq-emerald/10', edge: 'group-hover:border-bq-emerald' },
  { icon: 'workspace_premium', titleKey: 'landing.feature4Title', descKey: 'landing.feature4Desc', accent: 'text-bq-ruby', tint: 'bg-bq-ruby/10', edge: 'group-hover:border-bq-ruby' },
]

function FeaturesGrid() {
  const { t } = useTranslation()
  return (
    <section id="features" className="scroll-mt-24 py-16 sm:py-24 px-4 sm:px-6 bg-bq-paper" aria-label={t('landing.features')}>
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <h2 className="font-display text-3xl font-bold mb-4 text-bq-ink">{t('landing.features')}</h2>
          <div className="h-1 w-20 bg-bq-spectrum mx-auto rounded-full" />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {featureKeys.map((f) => (
            <div
              key={f.icon}
              className={`p-8 bg-bq-white border border-bq-hair shadow-bq-soft rounded-2xl hover:-translate-y-1 transition-all duration-300 border-b-2 ${f.edge} group`}
            >
              <div className={`w-14 h-14 rounded-xl ${f.tint} flex items-center justify-center ${f.accent} mb-6 group-hover:scale-110 transition-transform`}>
                <span className="material-symbols-outlined text-3xl">{f.icon}</span>
              </div>
              <h3 className="font-display text-xl font-bold mb-3 text-bq-ink">{t(f.titleKey)}</h3>
              <p className="text-bq-ink2">{t(f.descKey)}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}

/* ────────────────────────────── Try Now Section ─────────────────────────── */

function TryNowSection() {
  const { t } = useTranslation()
  return (
    <section className="py-16 sm:py-24 px-4 sm:px-6" aria-label="Trải nghiệm thử thách">
      <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-16 items-center">
        {/* Left info */}
        <div>
          <h2 className="font-display text-4xl font-bold mb-6 text-bq-ink">{t('landing.tryChallenge')}</h2>
          <p className="text-lg text-bq-ink2 mb-10 leading-relaxed">
            {t('landing.tryChallengeDesc')}
          </p>

          <div className="space-y-6">
            <div className="flex items-start gap-4 p-6 bg-bq-white border border-bq-hair shadow-bq-soft rounded-2xl border-l-4 border-l-bq-amber">
              <span className="material-symbols-outlined text-bq-amberd text-3xl mt-1">verified</span>
              <div>
                <h3 className="font-bold text-lg mb-1 text-bq-ink">{t('landing.updatedRegularly')}</h3>
                <p className="text-bq-ink2 text-sm">
                  {t('landing.updatedRegularlyDesc')}
                </p>
              </div>
            </div>

            <div className="flex items-start gap-4 p-6 bg-bq-white border border-bq-hair shadow-bq-soft rounded-2xl border-l-4 border-l-bq-sapphire">
              <span className="material-symbols-outlined text-bq-sapphire text-3xl mt-1">devices</span>
              <div>
                <h3 className="font-bold text-lg mb-1 text-bq-ink">{t('landing.multiPlatform')}</h3>
                <p className="text-bq-ink2 text-sm">
                  {t('landing.multiPlatformDesc')}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Right cards */}
        <div className="grid gap-6">
          {/* Daily Challenge Card */}
          <div className="bg-bq-white p-6 sm:p-8 rounded-3xl relative overflow-hidden group border border-bq-hair shadow-bq-soft">
            <span aria-hidden className="absolute top-0 inset-x-0 h-[5px] bg-bq-spectrum" />
            <div className="absolute top-3 right-3 sm:top-4 sm:right-4">
              <span className="px-3 py-1 bg-bq-amber/15 text-bq-amberd text-xs font-bold rounded-full whitespace-nowrap">
                {t('landing.popular')}
              </span>
            </div>
            <h3 className="font-display text-xl sm:text-2xl font-bold mb-2 pr-20 text-bq-ink">{t('landing.dailyChallenge')}</h3>
            <p className="text-bq-ink2 mb-6 text-sm sm:text-base">
              {t('landing.dailyChallengeDesc')}
            </p>
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-bq-inset text-bq-ink2 text-[11px] sm:text-xs font-bold whitespace-nowrap">
                <span className="material-symbols-outlined text-[14px]">group</span>
                {t('landing.playersCount')}
              </div>
              <Link
                to="/daily"
                className="bg-bq-action px-5 sm:px-6 py-2.5 sm:py-3 rounded-xl text-bq-ink shadow-bq-action font-bold text-sm sm:text-base active:scale-95 transition-transform inline-block whitespace-nowrap"
              >
                {t('gameModes.dailyBtn')}
              </Link>
            </div>
          </div>

          {/* Quick Practice Card */}
          <div className="bg-bq-white p-6 sm:p-8 rounded-3xl border border-bq-hair shadow-bq-soft hover:-translate-y-1 transition-transform">
            <h3 className="font-display text-xl sm:text-2xl font-bold mb-2 text-bq-ink">{t('landing.quickPractice')}</h3>
            <p className="text-bq-ink2 mb-6 text-sm sm:text-base">
              {t('landing.quickPracticeDesc')}
            </p>
            <Link
              to="/practice"
              className="block w-full py-3 sm:py-4 rounded-xl border border-bq-sapphire text-bq-sapphire font-bold hover:bg-bq-sapphire/10 transition-all active:scale-95 text-center"
            >
              {t('common.startNow')}
            </Link>
          </div>
        </div>
      </div>
    </section>
  )
}

/* ────────────────────────────── Leaderboard Preview ─────────────────────── */

// Hardcoded fallback — shown only while the live board loads or when it's
// empty (also keeps the SEO-prerendered HTML non-blank). Tier keys: OLD
// religious naming per DECISIONS.md 2026-04-19 (audience-driven).
const leaderboardData = [
  { rank: '01', initials: 'AN', name: 'Nguyễn Văn An', xp: '24,500', titleKey: 'tiers.apostle', top: true },
  { rank: '02', initials: 'LH', name: 'Lê Hồng Hạnh', xp: '21,200', titleKey: 'tiers.prophet', top: false },
  { rank: '03', initials: 'TM', name: 'Trần Minh', xp: '19,850', titleKey: 'tiers.sage', top: false },
  { rank: '10', initials: 'DP', name: 'Đặng Phương', xp: '12,400', titleKey: 'tiers.disciple', top: false },
]

/** First letter of first + last name word (e.g. "Nguyễn Văn An" → "NA"). */
function initialsOf(name: string): string {
  const parts = (name || '').trim().split(/\s+/).filter(Boolean)
  if (parts.length === 0) return '?'
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
}

function LeaderboardPreview() {
  const { t } = useTranslation()
  // Real top-10 national board from the no-auth public endpoint. Falls back to
  // the curated sample while loading / on error so the section never looks broken.
  const { data } = useQuery({
    queryKey: ['public-leaderboard', 'all-time', 10],
    queryFn: () => api.get('/api/public/leaderboard?period=all-time&size=10').then(r => r.data).catch(() => null),
    staleTime: 60_000,
  })
  const live = Array.isArray(data) ? data : []
  const isLive = live.length > 0
  const rows = isLive
    ? live.slice(0, 10).map((e: any, i: number) => ({
        rank: String(i + 1).padStart(2, '0'),
        initials: initialsOf(e.name),
        name: e.name || 'An danh',
        xp: (e.points ?? 0).toLocaleString(),
        titleKey: getTierByPoints(e.points ?? 0).nameKey,
        top: i === 0,
      }))
    : leaderboardData
  return (
    <section id="leaderboard" className="scroll-mt-24 py-16 sm:py-24 px-4 sm:px-6 bg-bq-paper" aria-label="Bảng xếp hạng">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-12">
          <div className="inline-block px-6 py-2 rounded-full bg-bq-action text-bq-ink shadow-bq-action font-extrabold text-lg mb-6">
            {import.meta.env.VITE_SEASON_NAME || t('landing.seasonDefault')}
          </div>
          <h2 className="font-display text-4xl font-extrabold tracking-tight text-bq-ink">{t('landing.nationalLeaderboard')}</h2>
        </div>

        <div className="bg-bq-white rounded-2xl sm:rounded-[2rem] overflow-hidden border border-bq-hair shadow-bq-soft">
          {/* Header row */}
          <div className="grid grid-cols-12 px-4 sm:px-8 py-3 sm:py-4 bg-bq-inset text-[12px] sm:text-xs font-bold text-bq-ink2">
            <div className="col-span-2">{t('landing.rank')}</div>
            <div className="col-span-7 sm:col-span-6">{t('landing.warrior')}</div>
            <div className="col-span-3 sm:col-span-2">XP</div>
            <div className="hidden sm:block sm:col-span-2 text-right">{t('landing.titleHeader')}</div>
          </div>

          <div className="divide-y divide-bq-hair">
            {rows.map((entry, idx) => (
              <div key={entry.rank}>
                <div
                  className={`grid grid-cols-12 px-4 sm:px-8 py-3 sm:py-5 items-center gap-2 ${
                    entry.top ? 'bg-bq-amber/10' : 'hover:bg-bq-inset transition-colors'
                  }`}
                >
                  <div className={`col-span-2 ${entry.top ? 'font-black text-xl sm:text-2xl text-bq-amberd' : 'font-bold text-bq-ink2'}`}>
                    {entry.rank}
                  </div>
                  <div className="col-span-7 sm:col-span-6 flex items-center gap-2 sm:gap-4 min-w-0">
                    <div
                      className={`w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center font-bold text-xs sm:text-base flex-shrink-0 ${
                        entry.top
                          ? 'bg-bq-amberd text-white'
                          : 'bg-bq-sapphire/10 text-bq-sapphire'
                      }`}
                    >
                      {entry.initials}
                    </div>
                    <span className={`font-bold truncate text-sm sm:text-base text-bq-ink ${entry.top ? 'sm:text-lg' : ''}`}>{entry.name}</span>
                  </div>
                  <div className={`col-span-3 sm:col-span-2 font-display font-extrabold tabular-nums text-[13px] sm:text-base ${entry.top ? 'text-bq-amberd' : 'text-bq-ink2'}`}>
                    {entry.xp}
                  </div>
                  <div
                    className={`hidden sm:block sm:col-span-2 text-right text-[9px] sm:text-xs font-bold uppercase truncate ${
                      entry.top ? 'text-bq-amberd' : idx < 3 ? 'text-bq-sapphire' : 'text-bq-ink2'
                    }`}
                  >
                    {t(entry.titleKey)}
                  </div>
                </div>

                {/* Ellipsis between rank 03 and rank 10 — fallback sample only
                    (it jumps 03→10); the live board is contiguous top-N. */}
                {!isLive && entry.rank === '03' && (
                  <div className="text-center py-4 text-bq-ink3">&bull;&bull;&bull;</div>
                )}
              </div>
            ))}
          </div>
        </div>

        <div className="text-center mt-8">
          {/* Guests see only the public teaser; the full board (with "your rank",
              tiers, seasons) needs an account, so funnel them to login rather
              than the bare guest /leaderboard view. */}
          <Link
            to="/login"
            className="inline-flex items-center gap-1.5 text-bq-amberd font-bold hover:underline"
          >
            {t('leaderboard.loginToViewBoard')}
            <span className="material-symbols-outlined text-lg">arrow_forward</span>
          </Link>
        </div>
      </div>
    </section>
  )
}

/* ────────────────────────────── Church Group Showcase ────────────────────── */

function ChurchGroupShowcase() {
  const { t } = useTranslation()
  return (
    <section className="py-16 sm:py-24 px-4 sm:px-6 relative" aria-label="Nhóm hội thánh">
      <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-16 items-center">
        {/* Image */}
        <div className="order-2 lg:order-1 relative">
                    <img
            alt="Gốc sồi cạnh nhà nguyện — nơi nhóm cùng học Kinh Thánh"
            width={600}
            height={400}
            loading="lazy"
            className="relative w-full aspect-[3/2] object-cover rounded-bq border-[3px] border-bq-ink shadow-bq-card z-10"
            src="/images/lk/place-chapel.webp"
          />

          {/* Floating stats */}
          <div className="absolute top-10 -right-6 bg-bq-white p-5 rounded-2xl z-20 border-[3px] border-bq-ink shadow-[0_4px_0_#1D2B22] hidden md:block">
            <div className="text-[14px] font-extrabold text-bq-ink mb-3">{t('landing.teamProgress')}</div>
            <div className="space-y-3">
              <div className="h-3 w-48 bg-bq-track border-2 border-bq-ink rounded-full overflow-hidden">
                <div className="h-full w-[75%] bg-bq-amber" />
              </div>
              <div className="h-3 w-48 bg-bq-track border-2 border-bq-ink rounded-full overflow-hidden">
                <div className="h-full w-[45%] bg-bq-leaf" />
              </div>
            </div>
          </div>
        </div>

        {/* Text + features */}
        <div className="order-1 lg:order-2 space-y-8">
          <h2 className="font-display text-4xl font-bold leading-tight text-bq-ink">{t('landing.churchGroup')}</h2>
          <p className="text-lg text-bq-ink2 leading-relaxed">
            {t('landing.churchGroupDesc')}
          </p>

          <ul className="space-y-4">
            <li className="flex items-center gap-3">
              <span className="material-symbols-outlined text-bq-emerald">qr_code_2</span>
              <span className="text-bq-ink font-medium">{t('landing.qrJoin')}</span>
            </li>
            <li className="flex items-center gap-3">
              <span className="material-symbols-outlined text-bq-emerald">analytics</span>
              <span className="text-bq-ink font-medium">{t('landing.weeklyReport')}</span>
            </li>
            <li className="flex items-center gap-3">
              <span className="material-symbols-outlined text-bq-emerald">emoji_events</span>
              <span className="text-bq-ink font-medium">{t('landing.internalTournament')}</span>
            </li>
          </ul>

          <Link
            to="/groups"
            className="lk-btn text-bq-ink text-[17px] no-underline"
          >
            {t('landing.createFreeGroup')}
          </Link>
        </div>
      </div>
    </section>
  )
}

/* ────────────────────────────── Daily Verse ──────────────────────────────── */

function DailyVerse() {
  const { t } = useTranslation()
  return (
    <section className="py-16 sm:py-24 px-4 sm:px-6 bg-bq-paper" aria-label="Câu Kinh Thánh mỗi ngày">
      <div className="max-w-4xl mx-auto text-center">
        <div className="w-16 h-1 bg-bq-spectrum mx-auto mb-10 rounded-full" />
        <div className="relative px-12">
          <span className="material-symbols-outlined text-7xl text-bq-amber/30 absolute -top-8 -left-2 scale-x-[-1]">
            format_quote
          </span>
          <blockquote className="font-literata text-3xl md:text-4xl font-light italic leading-relaxed text-bq-ink mb-8">
            &ldquo;{t('landing.verseText')}&rdquo;
          </blockquote>
          <cite className="text-xl font-bold text-bq-amberd not-italic">{t('landing.verseRef')}</cite>
        </div>
      </div>
    </section>
  )
}

/* ────────────────────────────── Footer CTA ───────────────────────────────── */

function FooterCTA() {
  const { t } = useTranslation()
  const handleGoogleSignup = () => {
    window.location.href = `${import.meta.env.VITE_API_BASE_URL || ''}/oauth2/authorization/google`
  }

  return (
    <section className="py-32 px-6 relative overflow-hidden text-center" aria-label="Đăng ký miễn phí">
      <div className="absolute inset-0 -z-10 bg-gradient-to-b from-bq-paper to-bq-amber/10" />
      <div className="max-w-2xl mx-auto space-y-10">
        <h2 className="font-display text-4xl md:text-5xl font-extrabold tracking-tight text-bq-ink">
          {t('landing.readyToExplore')}
        </h2>
        <p className="text-bq-ink2 text-lg">
          {t('landing.joinCommunity')}
        </p>
        <div className="flex flex-col items-center gap-4">
          <button
            onClick={handleGoogleSignup}
            className="lk-btn !bg-bq-white text-bq-ink text-[17px]"
          >
            <svg className="w-6 h-6" viewBox="0 0 24 24" aria-hidden>
              <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
              <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
              <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
              <path d="M12 5.38c1.62 0 3.06.56 4.21 1.66l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
            </svg>
            {t('auth.loginWithGoogle')}
          </button>
          <p className="text-sm text-bq-ink3">
            {t('auth.freeNoAds')}
          </p>
        </div>
      </div>
    </section>
  )
}

/* ────────────────────────────── Footer ───────────────────────────────────── */

function Footer() {
  const { t } = useTranslation()
  return (
    <footer className="w-full py-12 px-8 flex flex-col md:flex-row justify-between items-center gap-6 bg-bq-inset border-t border-bq-hair">
      <div className="flex flex-col items-center md:items-start gap-2">
        <div className="text-lg font-bold text-bq-amberd font-display">BibleQuiz</div>
        <p className="font-body text-sm leading-relaxed text-bq-ink2">
          {t('landing.copyright')}
        </p>
      </div>
      <div className="flex gap-8">
        <a className="font-body text-sm text-bq-ink2 hover:text-bq-amberd transition-colors" href="/cau-do-kinh-thanh">
          Câu đố Kinh Thánh
        </a>
        <a className="font-body text-sm text-bq-ink2 hover:text-bq-amberd transition-colors" href="/help">
          {t('nav.help')}
        </a>
        <a className="font-body text-sm text-bq-ink2 hover:text-bq-amberd transition-colors" href="/privacy">
          {t('landing.privacy')}
        </a>
        <a className="font-body text-sm text-bq-ink2 hover:text-bq-amberd transition-colors" href="/terms">
          {t('landing.terms')}
        </a>
      </div>
      <div className="flex gap-4">
        <div className="w-8 h-8 rounded-full bg-bq-white border border-bq-hair flex items-center justify-center text-bq-ink2 hover:text-bq-amberd transition-colors cursor-pointer">
          <span className="material-symbols-outlined text-lg">language</span>
        </div>
        <div className="w-8 h-8 rounded-full bg-bq-white border border-bq-hair flex items-center justify-center text-bq-ink2 hover:text-bq-amberd transition-colors cursor-pointer">
          <span className="material-symbols-outlined text-lg">share</span>
        </div>
      </div>
    </footer>
  )
}

/* ────────────────────────────── About Page ──────────────────────────────── */

/**
 * /gioi-thieu (2026-10-09): the long-form introduction that used to be the guest home. "/" is now
 * the one-screen crossroads (GuestHome); this page keeps the features, leaderboard preview and
 * church-group story for readers and search engines, and anyone can open it.
 */
export default function AboutPage() {
  return (
    <div data-testid="about-page" className="bg-bq-paper font-body text-bq-ink selection:bg-bq-amber selection:text-white min-h-dvh">
      <PageMeta
        title="Giới thiệu BibleQuiz – Trắc Nghiệm Kinh Thánh Online"
        description="BibleQuiz là trò chơi trắc nghiệm Kinh Thánh miễn phí: 5 câu thử thách mỗi ngày, luyện theo sách hoặc theo câu chuyện, đấu hạng toàn quốc và học cùng nhóm hội thánh."
        canonicalPath="/gioi-thieu"
      />
      <GuestHeader />
      <main>
        <HeroSection />
        <FeaturesGrid />
        <TryNowSection />
        <LeaderboardPreview />
        <ChurchGroupShowcase />
        <DailyVerse />
        <FooterCTA />
      </main>
      <Footer />
    </div>
  )
}
